import { Row, Col, Button, Form, Modal as AntModal } from "antd";
import { SendOutlined } from "@ant-design/icons";
import { useForm } from "react-hook-form";
import axios from "axios";
import Constants from "../../Utils/Constants";
import useToken from "../../Authentication/useToken";
import PropTypes from "prop-types";
import "./Styles.css";
import { useTranslation } from "react-i18next";
import CreateClinicModal from "../../Clinic/CreateClinicModal";
import { useEffect, useState } from "react";
import ProfessionalIdentityFields from "../shared/ProfessionalIdentityFields";
import ClinicSelectorField from "../shared/ClinicSelectorField";
import { isClinicManagerRole } from "../shared/roleUtils";

const doctorFieldKeys = {
  labelFirstName: "Physicians.label_first_name",
  placeholderFirstName: "Physicians.placeholder_enter_first_name",
  errorRequiredFirstName: "Physicians.error_required_first_name",
  errorFirstNameMinLength: "Physicians.error_first_name_min_length",
  labelLastName: "Physicians.label_last_name",
  placeholderLastName: "Physicians.placeholder_enter_last_name",
  errorRequiredLastName: "Physicians.error_required_last_name",
  errorLastNameMinLength: "Physicians.error_last_name_min_length",
  labelEmail: "Physicians.label_email",
  placeholderEmail: "Physicians.placeholder_enter_email",
  errorRequiredEmail: "Physicians.error_required_email",
  errorInvalidEmailFormat: "Physicians.error_invalid_email_format",
  labelConfirmEmail: "Physicians.label_confirm_email",
  placeholderConfirmEmail: "Physicians.placeholder_confirm_email",
  errorRequiredEmailConfirmation: "Physicians.error_required_email_confirmation",
  errorEmailMismatch: "Physicians.error_email_mismatch",
  labelPhoneNumber: "Physicians.label_phone_number",
  placeholderPhoneNumber: "Physicians.placeholder_phone_number",
  errorRequiredPhoneNumber: "Physicians.error_required_phone_number",
  errorInvalidPhoneNumber: "Physicians.error_invalid_phone_number",
};

function CreateDoctor({ refetchDoctors }) {
  const { t } = useTranslation("Professionals");
  const isClinicManager = isClinicManagerRole();

  const { token } = useToken();
  const [clinics, setClinics] = useState([]);
  const [clinicModalOpen, setClinicModalOpen] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();

  const openModal = (msg, isError) => {
    AntModal[isError ? "error" : "success"]({
      content: <p>{msg}</p>,
      okText: t("Physicians.button_close"),
      centered: true,
      onOk: () => {
        if (!isError) reset();
      },
    });
  };

  const fetchClinics = async () => {
    try {
      const res = await axios.get(`${Constants.SERVER_URL}/clinics?active=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const clinicsData = res.data || [];
      setClinics(clinicsData);
      if (isClinicManager && clinicsData.length === 1) {
        setValue("clinicId", clinicsData[0].id, { shouldValidate: true });
      }
    } catch (e) {
      openModal(e?.response?.data?.message || "Erreur lors du chargement des cliniques", true);
    }
  };

  useEffect(() => {
    if (token) fetchClinics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onSubmit = (data) => {
    const doctorData = {
      firstname: data.firstname,
      lastname: data.lastname,
      email: data.email,
      phoneNumber: data.phoneNumber,
      role: "doctor",
      clinicId: data.clinicId,
    };

    axios
      .post(`${Constants.SERVER_URL}/create-professional-user`, doctorData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        refetchDoctors();
        openModal(t("Physicians.success_physician_creation"), false);
      })
      .catch((err) => {
        const backendMsg = err?.response?.data?.message;
        openModal(
          (backendMsg && t(`Backend:${backendMsg}`)) ||
            t("Physicians.error_physician_creation"),
          true
        );
      });
  };

  return (
    <Row justify="center">
      <Col span={16}>
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <ProfessionalIdentityFields
            t={t}
            control={control}
            errors={errors}
            getValues={getValues}
            keys={doctorFieldKeys}
          />

          <Row gutter={16}>
            <Col span={12}>
              <ClinicSelectorField
                control={control}
                errors={errors}
                clinics={clinics}
                isClinicManager={isClinicManager}
                onOpenCreateClinic={() => setClinicModalOpen(true)}
              />

              {!isClinicManager && (
                <CreateClinicModal
                  open={clinicModalOpen}
                  onClose={() => setClinicModalOpen(false)}
                  onCreated={(clinic) => {
                    fetchClinics();
                    if (clinic?.id) setValue("clinicId", clinic.id);
                  }}
                />
              )}
            </Col>
          </Row>

          <Form.Item className="submit-button">
            <Button type="primary" htmlType="submit" icon={<SendOutlined />}>
              {t("Physicians.button_create_physician")}
            </Button>
          </Form.Item>
        </Form>
      </Col>
    </Row>
  );
}

CreateDoctor.propTypes = {
  refetchDoctors: PropTypes.func.isRequired,
};

export default CreateDoctor;
